'use strict';

import { InjectableClass } from '@fino/lib-injection';
import {
  FormControlTypes,
  NoLabelTypes,
  IbanDisplayMode,
} from './formControl.constants';

import moment from 'moment';

export class FormControlController extends InjectableClass {
  constructor(
    $scope,
    $timeout,
    $sce,
    $filter,
    uuid,
    formControl,
    formControlService
  ) {
    'ngInject';

    super({
      $scope,
      $timeout,
      $sce,
      $filter,
      uuid,
      formControl,
      formControlService,
    });
  }

  $onInit() {
    const { $timeout } = this;

    this.setDefaults();

    // Apply configuration and set defaults
    // NOTE: We're using timeout here to wait one digest cycle before
    // the setup starts to circument some issues caused by conflicts
    // and strange race conditions - have to find the exact problem.
    $timeout(() => {
      this.setupControlType();
      this.bindModelWatcher();
      this.bindUpdateListener();
    });
  }

  get errors() {
    if (this.validate) {
      return this.control.$error;
    }
    return {};
  }

  get control() {
    return (this.form && this.form[this.name]) || {};
  }

  get isValid() {
    return this.control && this.control.$valid;
  }

  get hasError() {
    // Ensure a form is given, otherwise we have no chance
    // for validation and their error messages
    if (!this.form) {
      return false;
    }
    const { $invalid, $touched } = this.control;
    const { $submitted } = this.form;

    return (
      $invalid &&
      this.doValidate &&
      ($touched || $submitted || this.manualValidation)
    );
  }

  get floatLabel() {
    const emptyModel = angular.isArray(this.model) && this.model.length === 0;
    const showLabel =
      this.disabled ||
      this.readonly ||
      this.hasError ||
      !!this.hasFocus ||
      !!this.model ||
      this.model === 0;

    return (
      this.label &&
      showLabel &&
      !emptyModel &&
      NoLabelTypes.indexOf(this.type) === -1
    );
  }

  get isRequired() {
    if (!this.doValidate) {
      return false;
    }

    if (angular.isFunction(this.required)) {
      return this.required(this.combinedModel);
    }
    return !!this.required;
  }

  get checked() {
    return !!this.combinedModel || (this.options && !!this.options.checked);
  }

  get pattern() {
    if (!this.doValidate) {
      return null;
    }
    return this.options && this.options.pattern;
  }

  get validate() {
    if (!this.doValidate) {
      return null;
    }
    return this.options && this.options.validate;
  }

  set combinedModel(value) {
    if (this.typeOptions.combinedModel && this.value) {
      return (this.model[this.value] = value);
    }
    this.model = value;
  }

  get combinedModel() {
    if (this.typeOptions.combinedModel && this.value) {
      return this.model[this.value];
    }
    return this.model;
  }

  setDefaults() {
    const { $sce, uuid } = this;

    this.IbanDisplayMode = IbanDisplayMode;

    // Initially set type configuration
    // NOTE: This will be re-called inside the setupControlType
    // in case of the control switches/updates its type.
    this.setTypeConfiguration();

    // Create unique id if necessary
    if (!this.id) {
      this.id = uuid.v4();
    }

    // Handle label and use it as placeholder fallback
    if (this.label) {
      if (!this.placeholder) {
        this.placeholder = this.label;
      }

      // Trustify the given label markup
      this.label = $sce.trustAsHtml(this.label);
    }

    // Create default empty typeOptions
    if (!this.typeOptions) {
      this.typeOptions = {};
    } else if (!this.typeOptions.bind) {
      // Create copy to avoid side effects caused by references
      this.typeOptions = angular.copy(this.typeOptions);
    }

    // Extend type with custom type modifier
    const { typeModifier } = this;
    if (typeModifier) {
      if (typeof typeModifier === 'function') {
        this.type = typeModifier(this.type, this);
      } else {
        this.type += this.typeModifier;
      }
    }

    //
    // Set type option specific flags
    //

    // Key which is being used by the ui-select selectables
    this.selectKey = this.typeOptions.selectKey || 'value';

    // Order selectables by their selectKeys (defaults to true)
    let orderKeys = angular.isDefined(this.typeOptions.orderKeys)
      ? this.typeOptions.orderKeys
      : true;
    if (orderKeys) {
      let orderBy = this.typeOptions.orderBy || this.selectKey;
      this.typeOptions.selectables = this.$filter('orderBy')(
        this.typeOptions.selectables,
        orderBy
      );
    }

    if (
      angular.isDefined(this.ieDisabled) &&
      angular.isUndefined(this.disabled)
    ) {
      this.disabled = this.ieDisabled;
    }
  }

  setTypeConfiguration() {
    const { formControl } = this;

    // Normalize the type, defaults to `text`
    if (!this.type) {
      this.type = FormControlTypes.DEFAULT;
    }

    // Apply type specific configurations provided by the formControl provider.
    if (formControl.types[this.type]) {
      angular.extend(this, formControl.types[this.type]);
    }
  }

  setupControlType() {
    // In case of a type switch (or update) we have to re-apply
    // the type configuration of the new given type.
    this.setTypeConfiguration();

    // Extend type with custom type options
    const { typeOptions } = this;
    if (typeOptions) {
      // Handle typeOptions settings
      if (typeOptions.ibanDisplayMode) {
        this.toggleIbanDisplayMode(typeOptions.ibanDisplayMode, true);
      }
    }

    // doValidate defaults to true
    if (angular.isUndefined(this.doValidate)) {
      this.doValidate = true;
    }

    // manualValidation defaults to false
    if (angular.isUndefined(this.manualValidation)) {
      this.manualValidation = false;
    }

    // Run type specific configurations
    switch (this.type) {
      // case FormControlTypes.IBAN: {
      //   // Assign given iban value to its model value
      //   this.assignIbanModelToModelValue();
      //   break;
      // }
      case FormControlTypes.DATE: {
        if (this.options) {
          // Set default value as model value when no model is given
          const { min, max } = this.typeOptions;
          const { defaultValue, minValue = min, maxValue = max } = this.options;
          if (
            angular.isUndefined(this.model) &&
            angular.isDefined(defaultValue)
          ) {
            this.model = angular.copy(defaultValue);
          }

          // Set min-date
          if (minValue) {
            this.typeOptions.minDate = this.createUnixDate(minValue, true);
          }

          // Set max-date
          if (maxValue) {
            this.typeOptions.maxDate = this.createUnixDate(maxValue, true);
          }
        }
        break;
      }
      case FormControlTypes.SEARCH: {
        // Default activate clear input icon for search field
        if (angular.isUndefined(this.typeOptions.clearInputEnabled)) {
          this.typeOptions.clearInputEnabled = true;
        }
      }
    }
  }

  bindModelWatcher() {
    // Move this crappy workaroung to handle string-to-object parsing
    switch (this.type) {
      case FormControlTypes.DATE: {
        this.modelValue = this.model;

        const bindModelValueWatcher = () => {
          this.$scope.$watch('vm.modelValue', (newVal, oldVal) => {
            // Ignore equal-value-changes and form reset (undefined newVal)
            // compare date strings since equal date objects are not same even with same values
            // otherwise endless loops can occure
            const maybeNewVal = newVal && newVal.toDateString();
            const maybeOldVal = oldVal && oldVal.toDateString();
            if (maybeNewVal === maybeOldVal || angular.isUndefined(newVal)) {
              return;
            }

            // Handle unsetting/reset of value
            if (this.modelValue === null) {
              this.model = null;
            }

            // Handle value selected/parsed by datepicker
            if (this.modelValue instanceof Date) {
              // Avoid local date by creating unix date from current model value
              let date = this.createUnixDate(this.modelValue);

              // ATTENTION: Break the watch loop to avoid endless circles
              // and annoying date issues.
              this.breakModelWatch = true;
              this.model = date.toISOString();

              // Set date validity true
              this.formControlService.setValidity(
                this.control,
                'dateInvalid',
                true
              );

              // Trigger validation for min- and maxDate
              const { minDate, maxDate } = this.typeOptions;
              if (minDate) {
                this.formControlService.setValidity(
                  this.control,
                  'min',
                  minDate <= date
                );
              }
              if (maxDate) {
                this.formControlService.setValidity(
                  this.control,
                  'max',
                  maxDate >= date
                );
              }
            } else if (this.isRequired) {
              // Trigger validation for invalid date (only if date is required and not kind of date)
              this.formControlService.setValidity(
                this.control,
                'dateInvalid',
                false
              );
            }
          });
        };

        const bindModelWatcher = () => {
          this.$scope.$watch('vm.model', (newVal, oldVal) => {
            if (this.breakModelWatch) {
              return (this.breakModelWatch = false);
            }

            let toBeParsed = this.model;
            const parsedGermanFormat = moment(this.model, 'DD.MM.YYYY');

            if (parsedGermanFormat.isValid()) {
              toBeParsed = parsedGermanFormat.toDate();
            }
            const value = this.model ? new Date(toBeParsed) : undefined;
            this.modelValue = value;
          });
        };

        bindModelWatcher();
        bindModelValueWatcher();

        break;
      }
      // case FormControlTypes.IBAN: {
      //   // Hack to ensure a random filled iban model in bcan mode (splitted input fields)
      //   // will be filled by applying the model to the modelValue.
      //   const hackyIbanModelWatcher = this.$scope.$watch('vm.model', (newVal, oldVal) => {
      //     if (newVal !== oldVal && this.ibanDisplayMode === IbanDisplayMode.BCAN) {
      //       this.$timeout(this.assignIbanModelToModelValue());
      //       hackyIbanModelWatcher(); // destroy the watcher
      //     }
      //   });
      //   break;
      // }
    }
  }

  bindUpdateListener() {
    const { $scope } = this;
    $scope.$on(`fdFormControl.update:${this.name}`, ($event, update) => {
      switch (update.type) {
        case 'typeOptions': {
          angular.extend(this.typeOptions, update.data);
          break;
        }
      }
    });
  }

  /**
   * Timezone takes effect and can lead to false date, therefore
   * we calculate the offset and subtract it from the given model value.
   *
   * @param {Object} date local date
   * @param {Boolean} [unsetHours=false] Optional unset hours of newly created date
   * @return {Object} Unix date
   */
  createUnixDate(date, unsetHours = false) {
    if (!date) {
      return null;
    }

    if (angular.isNumber(date)) {
      date = new Date(date);
    }

    if (angular.isString(date)) {
      date = new Date(date.replace(/\+\d{4}$/, ''));
    }

    const offset = date.getTimezoneOffset();
    let unixDate = new Date(date.getTime() - offset * 60000);
    if (unsetHours) {
      unixDate.setHours(0, 0, 0, 0);
    }
    return unixDate;
  }

  // Create dynamic element classes
  get inputContainerClass() {
    const classNames = [
      `${this.type}-container`,
      `form-control-name-${this.name}`,
    ];
    if (this.size) {
      classNames.push(this.size);
    }
    if (this.ibanDisplayMode) {
      classNames.push(this.ibanDisplayMode);
    }
    return classNames.join(' ');
  }

  showClearInput() {
    let show = false;
    if (this.typeOptions && this.typeOptions.clearInputEnabled) {
      show = !!this.model;
    }
    // Toggle icon with clear icon
    this.hideIcon = show;
    return show;
  }

  clearInput($event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.model = null;
  }

  getSelectModel(item) {
    if (this.typeOptions.modelKey) {
      return item[this.typeOptions.modelKey];
    }
    return item;
  }

  focus($event) {
    this.hasFocus = true;
    if (angular.isFunction(this.onFocus)) {
      this.onFocus($event);
    }
  }

  blur($event) {
    this.hasFocus = false;
    if (angular.isFunction(this.onBlur)) {
      this.onBlur($event);
    }
  }

  click($event) {
    if (angular.isFunction(this.onClick)) {
      this.onClick($event);
    }
  }

  keypress($event) {
    if (angular.isFunction(this.onKeypress)) {
      this.onKeypress($event);
    }
  }

  change($event) {
    if (angular.isFunction(this.onChange)) {
      this.onChange($event);
    }

    if (angular.isFunction(this.typeOptions.onChange)) {
      this.typeOptions.onChange(this.combinedModel, this);
    }
  }

  select($item, $model) {
    if (angular.isFunction(this.onSelect)) {
      this.onSelect($item, $model);
    }
  }

  remove($item, $model) {
    if (angular.isFunction(this.onRemove)) {
      this.onRemove($item, $model);
    }
  }

  upload(fileId) {
    this.model = fileId;
    if (angular.isFunction(this.onUpload)) {
      this.onUpload(fileId);
    }
  }

  uploadStart(upload) {
    if (angular.isFunction(this.onUploadStart)) {
      this.onUploadStart(upload);
    }
  }

  uploadFail(err) {
    if (angular.isFunction(this.onUploadFail)) {
      this.onUploadFail(err);
    }
  }

  uploadFinally() {
    if (angular.isFunction(this.onUploadFinally)) {
      this.onUploadFinally();
    }
  }

  toggleDatepicker($event) {
    // Prevent toggling when control is disabled/readonly
    if (this.disabled || this.readonly) {
      $event.preventDefault();
      return;
    }
    this.typeOptions.datepickerOpen = !this.typeOptions.datepickerOpen;
  }

  toggleIbanDisplayMode(mode, preventModel = false) {
    if (mode) {
      // Set given mode
      this.ibanDisplayMode = mode;
    } else {
      // Toggle current mode
      if (this.ibanDisplayMode !== IbanDisplayMode.BCAN) {
        this.ibanDisplayMode = IbanDisplayMode.BCAN;
      } else {
        this.ibanDisplayMode = IbanDisplayMode.IBAN;
      }
    }

    // Clean model on display mode change (if not prevented)
    if (!preventModel && !(this.disabled || this.readonly)) {
      delete this.model;
    }
  }

  isSelectChoiceDisabled(item) {
    return item.disabled;
  }
}
